import { routeArbor } from "@/setup/routes/catalog.routes";
import { ErrorModal } from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Autocomplete,
	Box,
	Chip,
	CircularProgress,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { Link } from "react-router-dom";
import { Arbor } from "../models/Arbor";
import { getArbors } from "../repositories/ArborRepository";
import { NewArborDialog } from "./NewArborDialog";

type Props = {
	label: string;
	value: number | null;
	onChange?: (partId: number | null, partName?: string) => void;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	onForceAssembly?: (force: boolean) => void;
	required?: boolean;
};

const useArbors = (partId?: number, onError?: (error: Error) => void) => {
	return useQuery<Arbor[], Error>({
		queryKey: ["arbors", partId],
		meta: { realtime: ["M2.GetArbors"] },
		queryFn: ({ signal }) => getArbors({ partId }, signal),
		onError: onError,
	});
};

export const ArborSelector = ({
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	onForceAssembly = () => {},
	required = false,
}: Props) => {
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [selectedValue, setSelectedValue] = useState<Arbor | null>(null);
	const [showNewArborDialog, setShowNewArborDialog] = useState(false);

	const { data: arbors, isLoading } = useArbors(undefined, (error) =>
		setError(error.message),
	);

	const selectedArbor = arbors?.find((arbor) => arbor.PartId === value) || null;

	// const inUse = (props: Arbor | null) => {
	// 	if (props?.InUse) return true;
	// 	return false;
	// };

	// const handleConfirmationClose = (result?: string) => {
	// 	setMessage("");
	// 	if (result === "OK") {
	// 		onForceAssembly(true);
	// 		onChange(selectedValue?.PartId ?? null, selectedValue?.PartName);
	// 		setSelectedValue(null);
	// 		return;
	// 	}
	// };

	return (
		<>
			<Autocomplete
				clearOnEscape
				autoHighlight
				options={arbors?.filter((a) => !a.InUse) || []}
				getOptionLabel={(option) => option.PartName}
				renderOption={(props, option) => {
					return (
						<Box
							{...props}
							//bgcolor={inUse(option) ? autocompleteOptionColor : ""}
							component="li"
							sx={{
								width: "100%",
								height: "100%",
								boxSizing: "border-box",
							}}
						>
							<Box component="span" sx={{ display: "block", width: "100%" }}>
								<Typography>
									{option.PartName}
									{/* <i>
										{inUse(option)
											? option?.PartId == value
												? " (Actual) "
												: " (In Use)"
											: ""}
									</i> */}
								</Typography>
							</Box>
							<Box
								style={{
									display: "flex",
									width: "100%",
								}}
							>
								<Chip
									size="small"
									color="primary"
									variant="outlined"
									label={`Stands: ${option?.Stands ?? "-"}`}
								/>
							</Box>
						</Box>
					);
				}}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						inputProps={{
							...params.inputProps,
							"data-test-id": "arbor-selector",
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										<>
											{params.InputProps.endAdornment}
											{!disabled && (
												<IconButton
													onClick={(e) => {
														e.stopPropagation();
														setShowNewArborDialog(true);
													}}
													size="small"
													title="Add new arbor"
													data-test-id="add-new-arbor-button"
												>
													<AddIcon fontSize="small" />
												</IconButton>
											)}
											{value ? (
												<IconButton
													component={Link}
													to={`${routeArbor}/${value}`}
													target="_blank"
													size="small"
													title="See in arbors"
												>
													<VisibilityIcon fontSize="small" />
												</IconButton>
											) : null}
										</>
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedArbor}
				onChange={(event, newValue) => {
					// if (inUse(newValue)) {
					// 	setMessage(
					// 		`The arbor ${newValue?.PartName} is already in use by another roll, do you want to disassemble it?`,
					// 	);
					// 	setSelectedValue(newValue);
					// } else
					onChange(newValue?.PartId ?? null, newValue?.PartName);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading arbors"
			/>
			{/* <ConfirmationDialogRaw
				title="CONFIRMATION"
				message={message}
				open={message !== ""}
				onClose={handleConfirmationClose}
			/> */}
			<NewArborDialog
				show={showNewArborDialog}
				onHide={(newPartId?: number, newPartName?: string) => {
					setShowNewArborDialog(false);
					if (newPartId) {
						onChange(newPartId, newPartName);
					}
				}}
			/>
		</>
	);
};
